import Footer from '@/components/Footer'
import { useWindowSize } from '@/hooks/helpers/useWindowSize'
import { useWatchMissingNetworks } from '@/hooks/useWatchMissingNetworks'
import { updateConsent } from '@/lib/gtm'
import { useTracking } from '@/providers/TrackingProvider'
import { useMemo, useRef } from 'react'
import { Route, Routes } from 'react-router-dom'
import { Slide, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { match } from 'ts-pattern'
import { acceptedService } from 'vanilla-cookieconsent'
import { useAccount, useAccountEffect } from 'wagmi'
import AnnouncementBar from './components/AnnouncementBar'
import DesktopMenu from './components/nav-bar/DesktopMenu'
import MobileMenu from './components/nav-bar/MobileMenu'
import { useCookieConsent } from './hooks/useCookieConsent'
import { useOnMount } from './hooks/useOnMount'
import { setupAbsoluteLabs } from './lib/absolute-labs'
import CookiePolicy from './pages/CookiePolicy'
import PrivacyPolicy from './pages/PrivacyPolicyPage'
import TermsAndConditions from './pages/TermsAndConditionsPage'
import WhitePepper from './pages/WhitePepper'
import Constitution from './pages/Constitution'
import Home from './pages/home/Home'
import PepperInc from './pages/pepper-inc/PepperInc'
import AdminGuard from './pages/admin/AdminGuard'
import PepperIncAdminPage from './pages/admin/Admin'
import { useIsAdmin } from './hooks/survey-factory-contract/useIsSurveyAdmin'
import 'react-datepicker/dist/react-datepicker.css'
import * as Sentry from '@sentry/react'
import useScrollNavigation from '@/hooks/useScrollNavigation.ts'
import FeedbackButton from '@/components/FeedbackButton.tsx'
import { FEEDBACK_URL, SHOW_ANNOUNCEMENT_BAR } from '@/config/constants.ts'
import { testIdFactory } from '@/utils/test-id-factory.ts'

const LINKS = [
	{ link: '/', label: 'Home', testId: testIdFactory.navigationHomeLink() },
	{ link: '/#faq', label: 'FAQ', testId: testIdFactory.navigationFAQLink() },
	{ link: '/pepper-inc', label: 'Pepper Inc', testId: testIdFactory.navigationPepperINCLink() },
	{ link: '/whitepepper', label: 'WhitePepper', testId: testIdFactory.navigationWhitePepperLink() },
	{ link: '/constitution', label: 'Constitution', testId: testIdFactory.navigationConstitutionLink() }
]

const TopMenu = () => {
	const { isMobile, isTablet } = useWindowSize()
	const { address = '0x0' } = useAccount()

	const { data: isAdminAccount } = useIsAdmin(address)

	const links = useMemo(
		() => [
			...(isAdminAccount
				? [
						{
							link: '/admin/pepper-inc',
							label: 'Mr. PEPPER',
							testId: testIdFactory.navigationMRPepperLink()
						}
					]
				: []),
			...LINKS,
			...(isMobile || isTablet
				? [
						{
							link: FEEDBACK_URL,
							label: 'Feedback',
							testId: testIdFactory.navigationFeedbackLink(),
							external: true
						}
					]
				: [])
		],
		[isAdminAccount, isMobile, isTablet]
	)
	return isMobile || isTablet ? <MobileMenu links={links} /> : <DesktopMenu links={links} />
}

const Watchers = () => {
	useWatchMissingNetworks()
	useScrollNavigation()

	const { sendEvent } = useTracking()
	const { run } = useCookieConsent()

	const absoluteLabsScriptSetupRef = useRef<ReturnType<typeof setupAbsoluteLabs>>()

	useOnMount(() => {
		run(
			config => ({
				...config,
				onFirstConsent: () => {
					updateConsent(acceptedService('ga', 'marketing'))
				},
				onChange: () => {
					updateConsent(acceptedService('ga', 'marketing'))
				}
			}),
			{
				onAccept: service => {
					match(service).with('absolute-labs', () => {
						const absoluteLabsScriptURL = import.meta.env.VITE_ABSOLUTELABS_SCRIPT_URL
						const absoluteLabsSiteId = import.meta.env.VITE_ABSOLUTELABS_SITE_ID

						if (!absoluteLabsScriptURL?.trim()) {
							console.error('Absolute Labs Script URL is not set')

							return
						}

						absoluteLabsScriptSetupRef.current = setupAbsoluteLabs(
							absoluteLabsScriptURL,
							absoluteLabsSiteId
						)
					})
				},
				onReject: service => {
					match(service).with('absolute-labs', async () => {
						;(await absoluteLabsScriptSetupRef.current)?.forEach(destroy => destroy())

						absoluteLabsScriptSetupRef.current = undefined
					})
				}
			}
		)
	})

	useAccountEffect({
		onConnect({ isReconnected, address, connector }) {
			Sentry.setUser({ address })
			!isReconnected && sendEvent('connect_wallet_successful', { provider: connector?.name })
		},
		onDisconnect() {
			Sentry.setUser(null)
		}
	})

	return null
}

const App = (): JSX.Element => {
	const { isMobile, isTablet } = useWindowSize()
	return (
		<div style={SHOW_ANNOUNCEMENT_BAR ? { paddingTop: 'var(--announcement-h, 2.5rem)' } : undefined}>
			<Watchers />
			{SHOW_ANNOUNCEMENT_BAR && <AnnouncementBar />}
			<TopMenu />
			{isMobile || isTablet ? null : <FeedbackButton />}
			<Routes>
				<Route path="/" element={<Home />} />
				<Route path="/whitepepper" element={<WhitePepper />} />
				<Route path="/constitution" element={<Constitution />} />
				<Route path="/privacy-policy" element={<PrivacyPolicy />} />
				<Route path="/terms-and-conditions" element={<TermsAndConditions />} />
				<Route path="/cookie-policy" element={<CookiePolicy />} />
				<Route path="/pepper-inc" element={<PepperInc />} />
				<Route element={<AdminGuard />}>
					<Route path="/admin/pepper-inc" element={<PepperIncAdminPage />} />
				</Route>
			</Routes>
			<Footer />
			<ToastContainer position="top-center" autoClose={2500} draggable transition={Slide} />
		</div>
	)
}

export default App
